import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { getPasteErrorCopy, PASTE_CHOICES } from '../utils/annotationClipboardCopy';

const BilingualTitle = ({ english, chinese }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography component="div" sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.35 }}>
      {english}
    </Typography>
    <Typography component="div" sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.35, color: '#3f4248', mt: 0.25 }}>
      {chinese}
    </Typography>
  </Box>
);

const Definition = ({ description, label }) => (
  <Tooltip
    arrow
    placement="top"
    title={(
      <Box sx={{ p: 0.5, maxWidth: 340 }}>
        <Typography sx={{ fontSize: 13, lineHeight: 1.45 }}>{description.english}</Typography>
        <Typography sx={{ fontSize: 13, lineHeight: 1.45, mt: 0.5 }}>{description.chinese}</Typography>
      </Box>
    )}
  >
    <IconButton size="small" aria-label={`About ${label} (关于此粘贴选项)`} sx={{ color: '#5c6470' }}>
      <InfoOutlinedIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);

const AnnotationPasteDialog = ({
  open,
  error = null,
  onChoose,
  onClose,
  onAcknowledge,
}) => {
  const errorCopy = error ? getPasteErrorCopy(error) : null;
  return (
    <Dialog
      open={Boolean(open)}
      onClose={error ? () => {} : onClose}
      disableEscapeKeyDown={Boolean(error)}
      maxWidth="sm"
      fullWidth
      aria-labelledby="annotation-paste-dialog-title"
      PaperProps={{ sx: { borderRadius: '14px', mx: 2, overflow: 'hidden' } }}
    >
      <DialogTitle id="annotation-paste-dialog-title" sx={{ px: { xs: 2.5, sm: 3 }, pt: 2.5, pb: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          {error
            ? <WarningAmberRoundedIcon color="warning" sx={{ fontSize: 28, mt: '1px' }} />
            : <ContentPasteOutlinedIcon sx={{ fontSize: 28, mt: '1px', color: '#27313f' }} />}
          <BilingualTitle
            english={error ? 'Paste Unavailable' : 'Paste Annotation Blocks'}
            chinese={error ? '无法粘贴' : '粘贴标注方块'}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pt: '4px !important', pb: error ? 0.5 : 2.5 }}>
        {errorCopy ? (
          <Box>
            <Typography component="div" sx={{ color: '#30343a', fontSize: 17, lineHeight: 1.5, overflowWrap: 'anywhere' }}>
              {errorCopy.english}
            </Typography>
            <Typography component="div" sx={{ color: '#30343a', fontSize: 17, lineHeight: 1.5, mt: 1, overflowWrap: 'anywhere' }}>
              {errorCopy.chinese}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gap: 1.25 }}>
            {PASTE_CHOICES.map(choice => (
              <Box
                key={choice.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  alignItems: 'stretch',
                  border: '1px solid #d5d9df',
                  borderRadius: '11px',
                  background: 'linear-gradient(180deg, #fff 0%, #f8f9fa 100%)',
                  overflow: 'hidden',
                  transition: 'border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease',
                  '&:hover': { borderColor: '#8d96a3', boxShadow: '0 6px 16px rgba(32,39,49,.10)', transform: 'translateY(-1px)' },
                  '&:focus-within': { outline: '3px solid rgba(52,103,214,.28)', outlineOffset: 1 },
                }}
              >
                <Button
                  aria-label={choice.label}
                  onClick={() => onChoose(choice.id)}
                  sx={{
                    display: 'block',
                    textAlign: 'left',
                    color: '#20242a',
                    px: 2,
                    py: 1.5,
                    borderRadius: 0,
                    textTransform: 'none',
                    '&:hover': { backgroundColor: 'transparent' },
                  }}
                >
                  <Typography component="div" sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.4 }}>{choice.title.english}</Typography>
                  <Typography component="div" sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.4, mt: 0.15 }}>{choice.title.chinese}</Typography>
                  <Typography component="div" sx={{ fontSize: 15, lineHeight: 1.45, color: '#555c66', mt: 0.75, overflowWrap: 'anywhere' }}>{choice.description.english}</Typography>
                  <Typography component="div" sx={{ fontSize: 15, lineHeight: 1.45, color: '#555c66', mt: 0.35, overflowWrap: 'anywhere' }}>{choice.description.chinese}</Typography>
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', pt: 1, pr: 0.75 }}>
                  <Definition description={choice.description} label={choice.title.english} />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, pb: 2, pt: 1.5 }}>
        {error ? (
          <Button
            autoFocus
            variant="contained"
            onClick={onAcknowledge}
            sx={{ minWidth: 112, borderRadius: '8px', backgroundColor: '#111', fontWeight: 700, textTransform: 'none', '&:hover': { backgroundColor: '#292929' } }}
          >
            OK (确定)
          </Button>
        ) : (
          <Button onClick={onClose} sx={{ color: '#343941', fontWeight: 700, textTransform: 'none' }}>
            Cancel (取消)
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AnnotationPasteDialog;
